/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useState, useEffect } from 'react';
import { ClockIcon, PlusIcon, TrashIcon, PauseIcon, PlayIcon } from '@heroicons/react/24/outline';
import { scheduleService, experimentService, TrainingSchedule, Experiment } from '../../services/registryApi';
import toast from 'react-hot-toast';

const Schedules: React.FC = () => {
  const [schedules, setSchedules] = useState<TrainingSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '',
    experiment_id: '',
    cron_expr: '',
    run_name: '',
    webhook_url: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [schedulesRes, expRes] = await Promise.all([
          scheduleService.list(),
          experimentService.list({ limit: 100 }),
        ]);
        setSchedules(schedulesRes.data.schedules || []);
        setExperiments(expRes.data.experiments || []);
      } catch {
        toast.error('Failed to load schedules');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCreate = async () => {
    if (!form.name || !form.experiment_id || !form.cron_expr) {
      toast.error('Name, experiment, and cron expression are required');
      return;
    }
    setCreating(true);
    try {
      const res = await scheduleService.create({
        name: form.name,
        experiment_id: form.experiment_id,
        cron_expr: form.cron_expr,
        run_name: form.run_name || undefined,
        webhook_url: form.webhook_url || undefined,
      });
      setSchedules((prev) => [res.data.schedule, ...prev]);
      setShowCreate(false);
      setForm({ name: '', experiment_id: '', cron_expr: '', run_name: '', webhook_url: '' });
      toast.success('Schedule created');
    } catch {
      toast.error('Failed to create schedule');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (sched: TrainingSchedule) => {
    try {
      const res = await scheduleService.update(sched.id, { enabled: !sched.enabled });
      setSchedules((prev) => prev.map((s) => (s.id === sched.id ? res.data.schedule : s)));
      toast.success(sched.enabled ? 'Schedule paused' : 'Schedule resumed');
    } catch {
      toast.error('Failed to update schedule');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this schedule? This cannot be undone.')) return;
    try {
      await scheduleService.delete(id);
      setSchedules((prev) => prev.filter((s) => s.id !== id));
      toast.success('Schedule deleted');
    } catch {
      toast.error('Failed to delete schedule');
    }
  };

  const CRON_PRESETS = [
    { label: 'Every hour', value: '0 * * * *' },
    { label: 'Every day at midnight', value: '0 0 * * *' },
    { label: 'Every Monday at 02:00', value: '0 2 * * 1' },
    { label: 'Every 6 hours', value: '0 */6 * * *' },
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3">
              <ClockIcon className="w-8 h-8 text-primary-500" />
              <h1 className="text-2xl font-bold text-white">Training Schedules</h1>
            </div>
            <p className="text-gray-400 mt-1">
              Automate recurring training runs with cron-based schedules.
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            New Schedule
          </button>
        </div>

        {/* Create Form */}
        {showCreate && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">Create Schedule</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Schedule Name *</label>
                <input
                  type="text"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="weekly-retrain"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Experiment *</label>
                <select
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={form.experiment_id}
                  onChange={(e) => setForm((f) => ({ ...f, experiment_id: e.target.value }))}
                >
                  <option value="">Select experiment…</option>
                  {experiments.map((exp) => (
                    <option key={exp.id} value={exp.id}>{exp.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Cron Expression * <span className="text-gray-500">(minute hour dom month dow)</span></label>
                <input
                  type="text"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="0 2 * * 1"
                  value={form.cron_expr}
                  onChange={(e) => setForm((f) => ({ ...f, cron_expr: e.target.value }))}
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  {CRON_PRESETS.map((p) => (
                    <button
                      key={p.value}
                      onClick={() => setForm((f) => ({ ...f, cron_expr: p.value }))}
                      className="px-2 py-0.5 text-xs bg-gray-600 hover:bg-gray-500 text-gray-300 rounded"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Run Name Prefix</label>
                <input
                  type="text"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="scheduled-run"
                  value={form.run_name}
                  onChange={(e) => setForm((f) => ({ ...f, run_name: e.target.value }))}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-400 mb-1">Webhook URL (optional)</label>
                <input
                  type="url"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="https://your-server.com/webhook"
                  value={form.webhook_url}
                  onChange={(e) => setForm((f) => ({ ...f, webhook_url: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleCreate}
                disabled={creating}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg disabled:opacity-50"
              >
                {creating ? 'Creating…' : 'Create Schedule'}
              </button>
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-gray-400 hover:text-white text-sm font-medium rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Schedule List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          </div>
        ) : schedules.length === 0 ? (
          <div className="bg-gray-900 rounded-xl border border-gray-700 p-16 text-center">
            <ClockIcon className="mx-auto h-12 w-12 text-gray-600 mb-3" />
            <h3 className="text-sm font-medium text-white mb-1">No schedules yet</h3>
            <p className="text-sm text-gray-400">
              Create a schedule to automatically re-train your models on a regular cadence.
            </p>
          </div>
        ) : (
          <div className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Cron</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Next Fire</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Last Fired</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Fires</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {schedules.map((sched) => (
                  <tr key={sched.id} className="hover:bg-gray-800">
                    <td className="px-6 py-4 text-sm font-medium text-white">{sched.name}</td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-300">{sched.cron_expr}</td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {sched.next_fire_at ? new Date(sched.next_fire_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {sched.last_fired_at ? new Date(sched.last_fired_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">{sched.fire_count}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${sched.enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
                        {sched.enabled ? 'Active' : 'Paused'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleToggle(sched)}
                          className="p-1.5 text-gray-400 hover:text-white rounded transition-colors"
                          title={sched.enabled ? 'Pause' : 'Resume'}
                        >
                          {sched.enabled ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDelete(sched.id)}
                          className="p-1.5 text-gray-400 hover:text-red-400 rounded transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Schedules;
